declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

function getUtmSource(): string {
  const stored = sessionStorage.getItem('utm_source');
  if (stored) return stored;

  const params = new URLSearchParams(window.location.search);
  const utm = params.get('utm_source') || 'direct';

  sessionStorage.setItem('utm_source', utm);
  return utm;
}

function track(eventName: string, params?: Record<string, any>) {
  const source = getUtmSource();
  window.gtag?.('event', eventName, {
    ...params,
    utm_source: source,
    channel: source,
    app: 'voucher_game',
  });
}

// Returns the GA4 client_id so the order create payload can carry it to the
// backend, which fires the server-side `purchase` event via Measurement
// Protocol with this id as `client_id`. Without it, GA4 treats the
// server-side event as a fresh anonymous session and acquisition / campaign
// attribution from the browsing session is lost. Resolves to an empty
// string if gtag never loads (adblock, slow script, etc.) within timeoutMs.
export function getGaClientId(timeoutMs: number = 100): Promise<string> {
  return new Promise((resolve) => {
    if (!window.gtag) {
      resolve('');
      return;
    }
    const timer = setTimeout(() => resolve(''), timeoutMs);
    try {
      window.gtag('get', getMeasurementId(), 'client_id', (clientId: string) => {
        clearTimeout(timer);
        resolve(typeof clientId === 'string' ? clientId : '');
      });
    } catch {
      clearTimeout(timer);
      resolve('');
    }
  });
}

// getGaSessionId resolves the GA4 session_id so the order create payload can
// carry it alongside client_id. The backend Measurement Protocol `purchase`
// event needs BOTH to stitch into the originating session — client_id alone
// lands the event as a fresh "Unassigned" session, breaking funnel
// attribution. Resolves to '' if gtag never loads within timeoutMs.
export function getGaSessionId(timeoutMs: number = 100): Promise<string> {
  return new Promise((resolve) => {
    if (!window.gtag) {
      resolve('');
      return;
    }
    const timer = setTimeout(() => resolve(''), timeoutMs);
    try {
      window.gtag('get', getMeasurementId(), 'session_id', (sessionId: unknown) => {
        clearTimeout(timer);
        // gtag returns session_id as a number; normalize to string.
        resolve(sessionId == null ? '' : String(sessionId));
      });
    } catch {
      clearTimeout(timer);
      resolve('');
    }
  });
}

// Voucher-game frontend ships gtag.js inline in index.html with the
// measurement id; the helper reads it back from the dataLayer config or
// falls back to a sentinel that GA4 ignores. Keep this aligned with the
// id in index.html.
function getMeasurementId(): string {
  // GA4 voucher-game property — kept in sync with index.html's gtag config.
  return 'G-LHQ1VZY8K3';
}

export const analytics = {
  viewGame: (gameId: number, gameName: string) => {
    track('view_game', { game_id: gameId, game_name: gameName });
  },

  selectItem: (gameId: number, itemId: number, itemName: string, price: number) => {
    track('select_item', { game_id: gameId, item_id: itemId, item_name: itemName, price });
  },

  beginCheckout: (gameName: string, itemName: string, price: number) => {
    track('begin_checkout', { game_name: gameName, item_name: itemName, value: price, currency: 'USD' });
  },

  // GA4-recommended intent-to-pay signal. Fires when the buyer submits
  // the order (right after the backend create call succeeds, before the
  // Mosan WebView navigation). The canonical `purchase` event is fired
  // server-side via the Measurement Protocol on the paid transition —
  // see api/internal/service/ga_measurement_protocol.go
  // (SendPPOBPurchaseEvent). Client-side `purchase` is intentionally
  // NOT fired here anymore because Mosan WebView checkout redirects the
  // buyer away before any tab-side analytics could fire, which used to
  // over-count revenue on every order regardless of payment outcome.
  addPaymentInfo: (reference: string, gameName: string, itemName: string, amount: number) => {
    track('add_payment_info', {
      transaction_id: reference,
      currency: 'USD',
      value: amount,
      item_category: gameName,
      item_name: itemName,
    });
  },

  paymentStarted: (reference: string) => {
    track('payment_started', { order_reference: reference });
  },

  paymentCompleted: (reference: string) => {
    track('payment_completed', { order_reference: reference });
  },

  searchTransaction: (query: string) => {
    track('search_transaction', { search_term: query });
  },

  login: () => {
    track('login');
  },

  signUp: () => {
    track('sign_up');
  },

  loginWithGoogle: () => {
    track('login_google');
  },
};
